import { useEffect, useState } from "react";

import { type FavoriteView, type LoggedMeal, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { appCopy } from "../lib/i18n/app";
import { Eyebrow, MonoMeta } from "./ui/primitives";

type Props = {
  householdId: string;
  onLogged: (meal: LoggedMeal) => void;
};

// Renamed from FavoritesSection — flatter list shown on Home as a horizontal
// strip (per handoff Home §Favorites). Up to 3 cards.
export function FavoritesStrip({ householdId, onLogged }: Props) {
  const { lang } = useLang();
  const t = appCopy[lang];
  const [favorites, setFavorites] = useState<FavoriteView[] | null>(null);
  const [loggingId, setLoggingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listFavorites(householdId)
      .then((favs) => {
        if (!cancelled) setFavorites(favs);
      })
      .catch(() => {
        // Non-fatal on home — silently skip.
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  async function handleLog(fav: FavoriteView) {
    setLoggingId(fav.id);
    try {
      const meal = await api.logFavorite(householdId, fav.id);
      if (meal) onLogged(meal);
      setFavorites((prev) =>
        prev
          ? prev.map((f) =>
              f.id === fav.id ? { ...f, useCount: f.useCount + 1, lastUsedAt: Date.now() } : f,
            )
          : prev,
      );
    } catch {
      // Swallow — toast surface lives at the page level if needed.
    } finally {
      setLoggingId(null);
    }
  }

  if (!favorites || favorites.length === 0) return null;
  const top = favorites
    .slice()
    .sort((a, b) => b.useCount - a.useCount || (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0))
    .slice(0, 3);

  return (
    <section style={{ marginTop: 22 }}>
      <Eyebrow>{t.favStripEyebrow}</Eyebrow>
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1" style={{ marginTop: 10 }}>
        {top.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => handleLog(f)}
            disabled={loggingId === f.id}
            className="flex-1 min-w-[110px] p-3 text-left transition-all hover:opacity-90 disabled:opacity-50"
            style={{
              background: "var(--surface)",
              borderRadius: 12,
              boxShadow: "0 0 0 1px var(--nav-border)",
            }}
          >
            <div className="text-[12px] font-semibold truncate" style={{ color: "var(--ink)" }}>
              {f.name}
            </div>
            <div className="flex justify-between items-baseline mt-1.5">
              <span
                className="tabular"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "var(--ink)",
                }}
              >
                {f.kcal}
              </span>
              <MonoMeta>×{f.useCount}</MonoMeta>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
